/*
 * MEMBER FEEDBACK (F4a) — product feedback, feature requests and support.
 *
 * Offered only when the server says collection is enabled; until live activation is separately
 * authorized it is not, so neither the Settings row nor the form is shown to anyone.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronRight, MessageSquarePlus, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/feedback`;

const KIND_LABELS = {
  product_feedback: "Product feedback",
  feature_request: "Feature request",
  support: "Support request",
};
const AREA_LABELS = {
  message: "Message", talk: "Talk", dreams: "Dreams", memory: "Memory",
  billing: "Billing", account: "Account", other: "Something else",
};

const useAvailability = () => {
  const [availability, setAvailability] = useState(null);
  useEffect(() => {
    axios.get(`${API}/availability`)
      .then(({ data }) => setAvailability(data))
      .catch(() => setAvailability({ enabled: false }));
  }, []);
  return availability;
};

export const FeedbackSettingsRow = () => {
  const navigate = useNavigate();
  const availability = useAvailability();
  if (!availability?.enabled) return null;
  return (
    <button className="settings-row" onClick={() => navigate("/feedback")}
      data-testid="settings-send-feedback-btn" style={{ width: "100%", textAlign: "left" }}>
      <div className="settings-row__icon"><MessageSquarePlus /></div>
      <div className="settings-row__content">
        <span className="settings-row__label">Send feedback</span>
        <span className="settings-row__subtitle">Ideas, requests or a problem to report</span>
      </div>
      <ChevronRight className="settings-row__chevron" />
    </button>
  );
};

const Choice = ({ value, label, selected, onSelect, testId }) => (
  <button type="button" onClick={() => onSelect(value)} data-testid={testId}
    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${
      selected ? "border-teal-300 bg-teal-300/15 text-teal-100" : "border-white/15 text-white/70 hover:border-white/35"}`}>
    {label}
  </button>
);

export default function FeedbackPage() {
  const navigate = useNavigate();
  const availability = useAvailability();
  const [kind, setKind] = useState(null);
  const [area, setArea] = useState(null);
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const max = availability?.max_text || 500;

  const submit = async () => {
    setStatus("sending");
    try {
      await axios.post(API, { kind, area, text: text.trim() || null });
      setStatus("sent");
    } catch (e) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen utility-surface px-6 py-10 text-white" data-testid="feedback-page">
      <div className="mx-auto max-w-xl">
        <button onClick={() => navigate("/settings")} className="mb-8 flex items-center gap-2 text-sm text-white/60 hover:text-white"
          data-testid="feedback-back-btn"><ArrowLeft className="h-4 w-4" /> Settings</button>
        <h1 className="mb-3 text-3xl font-light">Send feedback</h1>
        {availability && !availability.enabled && (
          <p className="text-white/60" data-testid="feedback-unavailable">Feedback isn't open yet.</p>
        )}
        {availability?.enabled && status === "sent" && (
          <p className="text-teal-100" data-testid="feedback-sent">Thank you — it reached us.</p>
        )}
        {availability?.enabled && status !== "sent" && (
          <div className="space-y-8">
            <p className="text-sm text-white/60">Support requests are kept apart from product feedback.</p>
            <div className="flex flex-wrap gap-2" data-testid="feedback-kind-group">
              {(availability.kinds || []).map((k) => (
                <Choice key={k} value={k} label={KIND_LABELS[k] || k} selected={kind === k}
                  onSelect={setKind} testId={`feedback-kind-${k}`} />
              ))}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="feedback-area-group">
              {(availability.areas || []).map((a) => (
                <Choice key={a} value={a} label={AREA_LABELS[a] || a} selected={area === a}
                  onSelect={setArea} testId={`feedback-area-${a}`} />
              ))}
            </div>
            <div>
              <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, max))}
                rows={5} maxLength={max} placeholder="Tell us more (optional)"
                className="w-full rounded-xl border border-white/15 bg-white/5 p-4 text-sm text-white placeholder:text-white/40 focus:border-teal-300 focus:outline-none"
                data-testid="feedback-text-input" />
              <div className="mt-1 text-right text-xs text-white/40" data-testid="feedback-text-count">{text.length}/{max}</div>
            </div>
            {status === "error" && (
              <p className="text-sm text-rose-300" data-testid="feedback-error">It couldn't be sent just now.</p>
            )}
            <button onClick={submit} disabled={!kind || !area || status === "sending"}
              className="rounded-full bg-teal-300 px-6 py-3 text-sm font-medium text-slate-900 transition-opacity duration-200 disabled:opacity-40"
              data-testid="feedback-submit-btn">
              {status === "sending" ? "Sending…" : "Send"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
